import tzData from './timezone.json';

type TzEntry = { value: string; abbr: string; offset: number; text: string; utc: string[] };

/**
 * Build lookup maps from timezone.json at module load time so lookups are O(1).
 * Handles:
 *   - Windows names  e.g. "Eastern Standard Time"
 *   - Abbreviations  e.g. "EST", "PDT"
 *   - UTC text       e.g. "(UTC-05:00) Eastern Time (US & Canada)"
 */
const byWindowsName = new Map<string, string>();
const byAbbrFromJson = new Map<string, string>();
const byText = new Map<string, string>();

for (const entry of tzData as TzEntry[]) {
  const iana = entry.utc[0];
  if (!iana) continue;
  byWindowsName.set(entry.value.toLowerCase(), iana);
  byAbbrFromJson.set(entry.abbr.toLowerCase(), iana);
  byText.set(entry.text.toLowerCase(), iana);
}

/**
 * Comprehensive worldwide timezone abbreviation → IANA map.
 * Where an abbreviation is ambiguous (e.g. IST = India / Israel / Ireland),
 * the most-populated / most-common interpretation is used.
 */
const ABBR_TO_IANA: Record<string, string> = {
  // UTC / Universal
  utc: 'UTC', gmt: 'Etc/GMT', z: 'UTC',
  // Americas — North
  hst: 'Pacific/Honolulu', hdt: 'Pacific/Honolulu',
  akst: 'America/Anchorage', akdt: 'America/Anchorage',
  pst: 'America/Los_Angeles', pdt: 'America/Los_Angeles',
  mst: 'America/Denver', mdt: 'America/Denver',
  cst: 'America/Chicago', cdt: 'America/Chicago',
  est: 'America/New_York', edt: 'America/New_York',
  ast: 'America/Halifax', adt: 'America/Halifax',
  nst: 'America/St_Johns', ndt: 'America/St_Johns',
  // Americas — Central & South
  cot: 'America/Bogota', ect: 'America/Guayaquil', pet: 'America/Lima',
  vet: 'America/Caracas', bot: 'America/La_Paz',
  art: 'America/Argentina/Buenos_Aires',
  brt: 'America/Sao_Paulo', brst: 'America/Sao_Paulo',
  gft: 'America/Cayenne', srt: 'America/Paramaribo',
  uyt: 'America/Montevideo', pyt: 'America/Asuncion',
  clt: 'America/Santiago', clst: 'America/Santiago',
  // Atlantic
  cvt: 'Atlantic/Cape_Verde', azot: 'Atlantic/Azores', azost: 'Atlantic/Azores',
  // Europe
  wet: 'Europe/Lisbon', west: 'Europe/Lisbon',
  bst: 'Europe/London',
  cet: 'Europe/Paris', cest: 'Europe/Paris',
  eet: 'Europe/Helsinki', eest: 'Europe/Helsinki',
  msk: 'Europe/Moscow', fet: 'Europe/Kaliningrad',
  trt: 'Europe/Istanbul', samt: 'Europe/Samara',
  // Africa
  wat: 'Africa/Lagos', cat: 'Africa/Harare',
  eat: 'Africa/Nairobi', sast: 'Africa/Johannesburg',
  // Middle East
  irst: 'Asia/Tehran', irdt: 'Asia/Tehran',
  idt: 'Asia/Jerusalem', gst: 'Asia/Dubai',
  // Asia
  ist: 'Asia/Kolkata', npt: 'Asia/Kathmandu',
  bdt: 'Asia/Dhaka', slt: 'Asia/Colombo',
  mmt: 'Asia/Rangoon', ict: 'Asia/Bangkok',
  wib: 'Asia/Jakarta', wita: 'Asia/Makassar', wit: 'Asia/Jayapura',
  hkt: 'Asia/Hong_Kong', sgt: 'Asia/Singapore',
  myt: 'Asia/Kuala_Lumpur', pht: 'Asia/Manila',
  jst: 'Asia/Tokyo', kst: 'Asia/Seoul',
  tlt: 'Asia/Dili', pgt: 'Pacific/Port_Moresby',
  yekt: 'Asia/Yekaterinburg', omst: 'Asia/Omsk', krat: 'Asia/Krasnoyarsk',
  irkt: 'Asia/Irkutsk', yakt: 'Asia/Yakutsk', vlat: 'Asia/Vladivostok',
  magt: 'Asia/Magadan', sakt: 'Asia/Sakhalin', pett: 'Asia/Kamchatka',
  azt: 'Asia/Baku', azst: 'Asia/Baku', get: 'Asia/Tbilisi',
  amt: 'Asia/Yerevan', kgt: 'Asia/Bishkek', tmt: 'Asia/Ashgabat',
  uzt: 'Asia/Tashkent', tjt: 'Asia/Dushanbe',
  pkt: 'Asia/Karachi', aft: 'Asia/Kabul',
  // Oceania
  aest: 'Australia/Sydney', aedt: 'Australia/Sydney',
  acst: 'Australia/Darwin', acdt: 'Australia/Adelaide',
  awst: 'Australia/Perth',
  nzst: 'Pacific/Auckland', nzdt: 'Pacific/Auckland',
  fjt: 'Pacific/Fiji', fjs: 'Pacific/Fiji',
  tot: 'Pacific/Tongatapu', tost: 'Pacific/Tongatapu',
  wst: 'Pacific/Apia', sst: 'Pacific/Pago_Pago',
  chst: 'Pacific/Guam', pont: 'Pacific/Pohnpei',
  mht: 'Pacific/Majuro', gilt: 'Pacific/Tarawa',
};

/**
 * Parses a timezone string into either:
 * - { type: 'iana'; key: string }   — a valid IANA key usable by Intl (includes Etc/GMT±N)
 * - { type: 'offset'; minutes: number } — a raw UTC-offset in minutes (for fractional offsets
 *   like GMT+5:30 that Intl can't handle directly)
 * - null if unparseable
 *
 * Handles all common formats:
 *   IANA              "America/New_York"
 *   GMT/UTC offset    "GMT-4", "UTC+5", "GMT+5:30", "+05:30", "-04:00"
 *   Windows name      "Eastern Standard Time"
 *   Abbreviation      "EST", "PDT"
 *   UTC text          "(UTC-05:00) Eastern Time (US & Canada)"
 *
 * The POSIX / Etc/GMT sign convention is the *inverse* of the common GMT±N notation:
 *   GMT-4  (UTC-4)  →  Etc/GMT+4
 *   GMT+5  (UTC+5)  →  Etc/GMT-5
 */
function parseTimezone(
  timezone: string,
): { type: 'iana'; key: string } | { type: 'offset'; minutes: number } | null {
  // 1. Try it as a plain IANA identifier first (e.g. "America/New_York", "UTC", "Etc/GMT+4")
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return { type: 'iana', key: timezone };
  } catch {
    // not a valid IANA identifier — fall through
  }

  const lower = timezone.toLowerCase().trim();

  // 2. Windows timezone name e.g. "Eastern Standard Time"
  const fromWindowsName = byWindowsName.get(lower);
  if (fromWindowsName) return { type: 'iana', key: fromWindowsName };

  // 3. Timezone abbreviation — comprehensive map first, json fallback
  const fromAbbr = ABBR_TO_IANA[lower] ?? byAbbrFromJson.get(lower);
  if (fromAbbr) return { type: 'iana', key: fromAbbr };

  // 4. UTC text format e.g. "(UTC-05:00) Eastern Time (US & Canada)"
  const fromText = byText.get(lower);
  if (fromText) return { type: 'iana', key: fromText };

  // 5. Bare "(UTC±HH:MM)" or "(UTC±H)" prefix e.g. "(UTC-4)" or "(UTC+05:30)"
  const utcPrefix = timezone.match(/^\(UTC([+-])(\d{1,2})(?::(\d{2}))?\)/i);
  if (utcPrefix) {
    const sign = utcPrefix[1] === '+' ? 1 : -1;
    const hours = parseInt(utcPrefix[2], 10);
    const mins = parseInt(utcPrefix[3] ?? '0', 10);
    const totalMinutes = sign * (hours * 60 + mins);
    if (mins === 0 && hours <= 14) {
      const etcKey = `Etc/GMT${sign > 0 ? '-' : '+'}${hours}`;
      try {
        Intl.DateTimeFormat(undefined, { timeZone: etcKey });
        return { type: 'iana', key: etcKey };
      } catch { /* fall through */ }
    }
    return { type: 'offset', minutes: totalMinutes };
  }

  // 6. Parse raw offset strings: "GMT-4", "UTC+5", "GMT+5:30", "+05:30", "-04:00", "GMT-04:00"
  const m = timezone.match(/^(?:GMT|UTC)?([+-])(\d{1,2})(?::(\d{2}))?$/i);
  if (m) {
    const sign = m[1] === '+' ? 1 : -1;
    const hours = parseInt(m[2], 10);
    const mins = parseInt(m[3] ?? '0', 10);
    const totalMinutes = sign * (hours * 60 + mins);

    // Whole-hour offsets within ±14 h can be expressed as Etc/GMT±N (sign flipped!)
    if (mins === 0 && hours <= 14) {
      const etcKey = `Etc/GMT${sign > 0 ? '-' : '+'}${hours}`;
      try {
        Intl.DateTimeFormat(undefined, { timeZone: etcKey });
        return { type: 'iana', key: etcKey };
      } catch {
        // shouldn't happen for valid ±14 h, but fall through just in case
      }
    }

    // Fractional offsets (e.g. +05:30) — must be handled by manually shifting the date
    return { type: 'offset', minutes: totalMinutes };
  }

  return null;
}

/**
 * Shifts an ISO date string by `offsetMinutes` and returns a new ISO string
 * that, when interpreted as UTC (timeZone: 'UTC'), shows the correct local time.
 */
function shiftByOffset(iso: string, offsetMinutes: number): string {
  const ms = new Date(iso).getTime() + offsetMinutes * 60_000;
  return new Date(ms).toISOString();
}

/** Returns the timezone string only if it's a valid IANA identifier, otherwise undefined. */
export function resolveTimezone(timezone?: string): string | undefined {
  if (!timezone) return undefined;
  const parsed = parseTimezone(timezone);
  return parsed?.type === 'iana' ? parsed.key : undefined;
}

/** Formats an ISO date string as e.g. "Wed, May 13, 2026", respecting the event's timezone. */
export function formatDate(iso?: string, timezone?: string): string {
  if (!iso) return '—';

  const parsed = timezone ? parseTimezone(timezone) : null;

  if (parsed?.type === 'iana') {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: parsed.key,
    }).format(new Date(iso));
  }

  if (parsed?.type === 'offset') {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(shiftByOffset(iso, parsed.minutes)));
  }

  // No timezone — use browser local time
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso));
}

/** Formats an ISO date string as a time e.g. "6:00 AM (GMT-4)". */
export function formatTime(iso?: string, timezone?: string): string {
  if (!iso) return '—';

  const parsed = timezone ? parseTimezone(timezone) : null;
  const tzLabel = timezone ? ` (${timezone})` : '';

  if (parsed?.type === 'iana') {
    const formatted = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: parsed.key,
    }).format(new Date(iso));
    return `${formatted}${tzLabel}`;
  }

  if (parsed?.type === 'offset') {
    const formatted = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    }).format(new Date(shiftByOffset(iso, parsed.minutes)));
    return `${formatted}${tzLabel}`;
  }

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));
}

/** Formats an ISO date string as e.g. "June 29, 2026 — 6:00 AM (GMT-4)". */
export function formatDateTimeLong(iso?: string, timezone?: string): string {
  if (!iso) return '—';

  const parsed = timezone ? parseTimezone(timezone) : null;
  const tzLabel = timezone ? ` (${timezone})` : '';

  const dateOpts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  let date: Date;
  let tz: string | undefined;

  if (parsed?.type === 'iana') {
    date = new Date(iso);
    tz = parsed.key;
  } else if (parsed?.type === 'offset') {
    date = new Date(shiftByOffset(iso, parsed.minutes));
    tz = 'UTC';
  } else {
    date = new Date(iso);
    tz = undefined;
  }

  const datePart = new Intl.DateTimeFormat('en-US', {
    ...dateOpts,
    ...(tz ? { timeZone: tz } : {}),
  }).format(date);

  const timePart = new Intl.DateTimeFormat('en-US', {
    ...timeOpts,
    ...(tz ? { timeZone: tz } : {}),
  }).format(date);

  return `${datePart} — ${timePart}${tzLabel}`;
}
